/**
 * 【S端】用户档案 · 用户统计（打卡概览 / 单日打卡明细）
 *
 * 契约真源：litta-workwx `api_reference/MemberUserSignStatApi/`（另见同目录 frontend.md，含 SIT 实测样例）。
 * 后端接口：com.litta.workwx.api.front.MemberUserSignStatApi
 *   - overview     近 N 周概览指标 + 稠密热力图（恒 364 格）
 *   - dailyDetail  单日打卡明细（按 5 大展示分类分组）
 *
 * ⚠️ 请求外层字段是 **`requestData`**（`BaseRequest<T>`），响应外层才是 `data`。
 *    传 `data` 会被后端拦成 `99998 请求数据为空`。
 *
 * ⚠️ 返回形状刻意是 `{ ok, reason, code, msg, data }` 而不是裸 data：
 *    **后端故障与"这个用户没打卡"不能塌成同一个值**，否则页面会把查询失败画成一片空白。
 *
 * reason 取值：
 *   null            成功
 *   BAD_REQUEST     99998 参数问题（日期没补零 / requestData 漏了）
 *   UPSTREAM_ERROR  99999 后端取数失败（底层 behavior RPC 异常）
 *   NETWORK_ERROR   网络层失败
 *   MALFORMED_RESPONSE 响应不是预期对象
 */
import moment from 'moment';
import $x from '@/utils/$x';

const API_PATHS = {
  overview: '/com.litta.workwx.api.front.MemberUserSignStatApi/overview',
  dailyDetail: '/com.litta.workwx.api.front.MemberUserSignStatApi/dailyDetail',
};

/** 本地起不了后端时置 true；联调/上线必须 false（走真实接口）。 */
export const USE_SIGN_MOCK = false;

const FAILURE_REASONS = {
  99998: 'BAD_REQUEST',
  99999: 'UPSTREAM_ERROR',
};

/** 统一信封处理：把 code≠00000 和网络失败都翻译成 ok:false + reason。 */
function unwrap(res) {
  if (!res || typeof res !== 'object') {
    return {
      ok: false,
      reason: 'MALFORMED_RESPONSE',
      code: null,
      msg: null,
      data: null,
    };
  }
  const code = res.code === undefined ? null : String(res.code);
  if (code !== '00000') {
    return {
      ok: false,
      code,
      msg: res.msg || null,
      reason: FAILURE_REASONS[code] || 'UPSTREAM_ERROR',
      data: null,
    };
  }
  const payload = res.data !== undefined ? res.data : null;
  if (!payload || typeof payload !== 'object') {
    return {
      ok: false,
      reason: 'MALFORMED_RESPONSE',
      code,
      msg: res.msg || null,
      data: null,
    };
  }
  return { ok: true, reason: null, code, msg: res.msg || null, data: payload };
}

async function call(path, requestData) {
  if (USE_SIGN_MOCK)
    return unwrap({ code: '00000', msg: 'OK', data: MOCK[path] });
  try {
    // axios 拦截器已把响应拆到 res.data，这里拿到的就是 { code, msg, data }
    const res = await $x.post(path, { requestData });
    return unwrap(res);
  } catch (error) {
    return {
      ok: false,
      reason: 'NETWORK_ERROR',
      code: null,
      msg: error?.message || null,
      data: null,
    };
  }
}

/* ------------------------------------------------------------------ *
 * 一、接口
 * ------------------------------------------------------------------ */

/**
 * 概览 + 热力图。
 * @param {{ userId: number|string, weeks?: number }} params userId 必填
 */
export function getSignOverview(params) {
  const { userId, weeks } = params || {};
  const requestData = { userId: Number(userId) };
  if (weeks) requestData.weeks = Number(weeks);
  return call(API_PATHS.overview, requestData);
}

/**
 * 单日打卡明细。
 * @param {{ userId: number|string, date: string, showDetail?: boolean }} params date 必须 yyyy-MM-dd 补零
 */
export function getSignDailyDetail(params) {
  const { userId, date, showDetail = true } = params || {};
  return call(API_PATHS.dailyDetail, {
    userId: Number(userId),
    date,
    showDetail,
  });
}

/* ------------------------------------------------------------------ *
 * 二、展示分类：后端枚举 ↔ 前端格子类型 key
 * ------------------------------------------------------------------ */

/**
 * 格子内自上而下的堆叠顺序（与 CalendarHeatmap 的 types 约定一致）。
 * key 沿用热力图组件已有的 train/sport/store/diet/other，
 * value 是后端 SignHeatmapDayCell 的字段名。
 */
export const CLOCK_TYPES = [
  { key: 'train', label: '训练打卡', color: '#fa8c16', field: 'trainingCount' },
  { key: 'sport', label: '运动打卡', color: '#52c41a', field: 'sportCount' },
  { key: 'store', label: '到店打卡', color: '#722ed1', field: 'offlineCount' },
  { key: 'diet', label: '饮食打卡', color: '#1890ff', field: 'dietCount' },
  { key: 'other', label: '其他打卡', color: '#eb2f96', field: 'otherCount' },
];

/**
 * 后端稠密 heatmap[] → CalendarHeatmap 需要的 weeks[] 分桶结构。
 *
 * 输入：[{ date, hasSign, trainingCount, sportCount, offlineCount, dietCount, otherCount, totalCount }]
 * 输出：[{ weekStart, days: [{ date, dayOfWeek, counts: { train, sport, store, diet, other }, total }] }]
 *
 * 两个必须留意的点：
 * 1. 只收 hasSign 的日期。组件对“缺格子”渲染空格，对“全 0 的格子”会照常画色块。
 * 2. **没有打卡的周也必须进数组**：CalendarHeatmap 的横轴是数组下标，
 *    抽掉空周会把 52 周压成十几列，日期与位置全错。
 * 3. 364 天不一定从周一开始，按自然周（周一起始）补齐后通常得到 53 列
 *    （首尾各一个残周），这是真实数据的固有形状，不做裁剪。
 */
export function toHeatmapWeeks(heatmap) {
  if (!Array.isArray(heatmap) || heatmap.length === 0) return [];
  const cells = heatmap.filter((c) => c && c.date && parseDay(c.date));
  if (cells.length === 0) return [];

  const days = new Map(); // date -> counts 项，仅有打卡的日
  cells.forEach((cell) => {
    if (!cell.hasSign) return;
    const m = parseDay(cell.date);
    if (!m) return;
    const counts = {};
    CLOCK_TYPES.forEach((t) => {
      const n = Number(cell[t.field] || 0);
      if (n > 0) counts[t.key] = n;
    });
    days.set(cell.date, {
      date: cell.date,
      dayOfWeek: m.isoWeekday(),
      counts,
      total: Number(cell.totalCount || 0),
    });
  });

  // 覆盖从首日周一到末日所在周一的全部自然周（含空周）
  const dates = cells.map((c) => c.date).sort();
  const firstMonday = mondayOf(parseDay(dates[0]));
  const lastMonday = mondayOf(parseDay(dates[dates.length - 1]));

  const weeks = [];
  for (
    let cur = firstMonday.clone();
    !cur.isAfter(lastMonday);
    cur.add(1, 'weeks')
  ) {
    const weekStart = cur.format('YYYY-MM-DD');
    const bucket = [];
    for (let dow = 1; dow <= 7; dow += 1) {
      const d = cur.clone().add(dow - 1, 'days');
      const hit = days.get(d.format('YYYY-MM-DD'));
      if (hit) bucket.push(hit);
    }
    weeks.push({ weekStart, days: bucket });
  }
  return weeks;
}

/** 解析 yyyy-MM-dd；非法返回 null（后端可能给 null/空串）。 */
function parseDay(date) {
  const m = moment(date, 'YYYY-MM-DD');
  return m.isValid() ? m : null;
}

/**
 * 该日的周一。用 isoWeekday()（核心 API）推，不用 startOf('isoWeek')——
 * 后者依赖 moment 的 isoWeek 插件，本仓未确认已加载。
 */
function mondayOf(m) {
  return m
    .clone()
    .startOf('day')
    .subtract(m.isoWeekday() - 1, 'days');
}

/* ------------------------------------------------------------------ *
 * 三、明细项归一化：后端字段语言不统一，页面只认 view 字段
 * ------------------------------------------------------------------ */

/**
 * 上课地点 / 到店地点取值。
 *
 * SIT 实测（2026-09-30）两种形状并存，必须都兜住：
 *   1. 纯文本门店名 —— 线下运动到店、碰一碰/扫码到店（底层按 storeId/tenantId 富化）
 *      例：'金证科技园三号楼…'
 *   2. **整坨 JSON 字符串** —— 线下真人指导课（私教）的 location 是底层原样存的定位对象：
 *      '{"address":"FEELINGME乐刻私教馆(朗悦居店)","latitude":"30.27","name":"朗悦居十号楼…"}'
 *      不解开就会把一坨代码画到用户屏幕上。
 *   3. 空串 / null —— 视频跟练、入镜指导等没有地点。
 *
 * 取值优先级：`address`（店招名）> `name`（详细地址）> null。
 * 形似 JSON 却解不出来（脏数据/被截断）返回 null：宁可不显示，也不显示一坨代码。
 */
export function parseLocation(raw) {
  if (raw === undefined || raw === null) return null;
  if (typeof raw === 'object') {
    return cleanText(raw.address) || cleanText(raw.name) || null;
  }
  const text = String(raw).trim();
  if (!text) return null;
  const looksStructured = /^[{[]/.test(text) || /"address"\s*:/.test(text);
  if (!looksStructured) return text;
  try {
    const parsed = JSON.parse(text);
    if (typeof parsed === 'string') return parsed.trim() || null;
    if (Array.isArray(parsed)) {
      return parsed.length === 1 ? parseLocation(parsed[0]) : null;
    }
    if (parsed && typeof parsed === 'object') {
      return cleanText(parsed.address) || cleanText(parsed.name) || null;
    }
    return null;
  } catch (e) {
    return null;
  }
}

/** 折叠空白、去首尾空格；空串归一为 null。 */
function cleanText(v) {
  if (v === undefined || v === null) return null;
  const s = String(v).replace(/\s+/g, ' ').trim();
  return s || null;
}

/**
 * 只放行含中文的文案。底层 type 这类字段有两种脸：中文（入镜指导）或枚举码
 * （RUN、midnight_snack、nfc_touch），码上界面就是事故（SIT 已撞过三次）。
 * 统一走这个闸门：不含中文一律不显示，宁缺毋滥。
 */
function cnText(v) {
  const s = cleanText(v);
  return s && /[\u4e00-\u9fa5]/.test(s) ? s : null;
}

/** 空值用「—」占位，避免卡片出现「真人：」后面空一片。 */
export function dashIfNull(v) {
  return v === undefined || v === null || v === '' ? '—' : v;
}

/**
 * 概览的训练拆分能不能展示：后端三档相加必须等于 trainingCount，
 * 不等（或 trainingSplitAvailable=false）时后端已把三档置空，
 * 页面就退回只显示合并数——不能拿半截数据去拆，
 * 否则用户看到的是加起来对不上的两个数。
 */
export function hasUsableTrainingSplit(overview) {
  const o = overview || {};
  if (o.trainingSplitAvailable === false) return false;
  const { trainingLiveCount, trainingMirrorCount, trainingOtherCount } = o;
  if (
    trainingLiveCount == null ||
    trainingMirrorCount == null ||
    trainingOtherCount == null
  ) {
    return false;
  }
  const sum =
    Number(trainingLiveCount) +
    Number(trainingMirrorCount) +
    Number(trainingOtherCount);
  return sum === Number(o.trainingCount || 0);
}

/**
 * 概览“打卡数”卡的分类明细：从 CLOCK_TYPES 直接映射后端计数字段。
 *
 * 为什么从五类现算而不是直接用 totalSignCount：
 * 卡片副标题列出的项必须与大数字对得上。之前副标题只列了饮食/运动/其他，
 * 而大数字是五类合计（含训练、到店），用户一眼就算不平（实测 88+84+60=232 vs 572）。
 * 拿这里当唯一口径源，副标题与大数字同源，永远相加相等。
 *
 * @returns {{key,label,color,field,value:number}[]}
 */
export function getSignBreakdown(overview) {
  const o = overview || {};
  return CLOCK_TYPES.map((t) => ({
    ...t,
    /** 副标题用短名：训练 / 运动 / 到店 / 饮食 / 其他 */
    shortLabel: t.label.replace('打卡', ''),
    value: Number(o[t.field] || 0),
  }));
}

/**
 * 餐次码 → 中文。取值来自 C 端权威表 litta-mini-new
 * src/packageD/editPhoto/components/food-type.vue（breakfast/lunch/dinner/snack/midnight_snack/drink）。
 * 注意：C 端这份表还会走接口 getDietTypeList 动态下发，新餐次码可能凭空出现，
 * 所以未知码一律不显示英文原值（只提醒一次），宁可不显示也不漏代码。
 */
const DIET_MEAL_LABEL = {
  breakfast: '早餐',
  lunch: '午餐',
  dinner: '晚餐',
  snack: '零食',
  midnight_snack: '宵夜',
  drink: '饮品',
};
const warnedDietCodes = new Set();

/** 餐次中文；未知码只提醒一次、不返回英文。 */
function dietMealLabel(code) {
  if (!code) return null;
  const label = DIET_MEAL_LABEL[code];
  if (label) return label;
  if (!warnedDietCodes.has(code)) {
    warnedDietCodes.add(code);
    // eslint-disable-next-line no-console
    console.warn('[打卡明细] 未知餐次码，已隐藏不显示英文：', code);
  }
  return null;
}

/* 时长统一用下方 formatDurationCn（带秒、带「用时」口径），不再保留四舍五入到分的版本 */

/**
 * `image` 字段是 JSON 字符串（形如 '["https://a.png"]'），不是数组，必须 parse。
 * 解析失败或空数组返回 []，让调用方按"无图"处理。
 */
export function parseImages(image) {
  if (!image) return [];
  if (Array.isArray(image)) return image.filter(Boolean);
  if (typeof image !== 'string') return [];
  const text = image.trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (Array.isArray(parsed)) return parsed.filter(Boolean);
    return parsed ? [String(parsed)] : [];
  } catch (e) {
    // 兼容单URL裸串
    return /^https?:\/\//.test(text) ? [text] : [];
  }
}

/**
 * 底层把图片与视频统一放在 `image` 字段里（实测 SIT 1000001966：自由打卡既有 .png 也有
 * 腾讯云点播直链 `https://xxx.vod2.myqcloud.com/.../v.f100030.mp4`），所以不能当作图片一律渲染。
 * 无后缀或后缀不认识时归为图片（保持原有行为，不至于把图误判成视频）。
 */
const VIDEO_EXT_RE = /\.(mp4|mov|m4v|webm|avi|flv|mkv|ts|m3u8)(\?|#|$)/i;
export function isVideoUrl(url) {
  const text = cleanText(url);
  if (!text) return false;
  if (VIDEO_EXT_RE.test(text)) return true;
  /** 云点播域名即使没后缀也是视频 */
  return /\.vod[0-9]*\.myqcloud\.com|\.vdl\.tc/i.test(text);
}

/** 按原始顺序拆成媒体列表，图文混排时保持会员端看到的顺序（PRD：图片在前、文字在后） */
export function splitMedia(image) {
  const media = parseImages(image).map((url) => ({
    url,
    kind: isVideoUrl(url) ? 'video' : 'image',
    cover: null,
  }));
  return {
    media,
    images: media.filter((m) => m.kind === 'image').map((m) => m.url),
    videos: media.filter((m) => m.kind === 'video').map((m) => m.url),
  };
}

/**
 * 归一化一条打卡的媒体列表 → [{ kind: 'image' | 'video', url, cover }]。
 *
 * 优先用后端的 `media` 字段：它带权威 type 和视频封面，且 **8 类打卡全量下发**
 * （2026-10-08 后端补齐，产品要求所有分类有图就展示）。
 * `media` 缺失（后端降级、或老数据）时回落 `image`，按后缀 / 点播域名猜类型。
 */
export function toMediaList(item) {
  const fromBackend = Array.isArray(item?.media) ? item.media : [];
  if (fromBackend.length) {
    const list = fromBackend
      .filter((m) => m && m.url)
      .map((m) => ({
        url: String(m.url),
        kind:
          m.type === 'video' || (m.type !== 'image' && isVideoUrl(m.url))
            ? 'video'
            : 'image',
        cover: m.cover || null,
      }));
    if (list.length) return list;
  }
  return splitMedia(item?.image).media;
}

/** 后端分类码 → 中文（与 MemberSignCategoryEnum 一字不差）。 */
const CATEGORY_LABEL = {
  training: '训练打卡',
  sport: '运动打卡',
  offline: '到店打卡',
  diet: '饮食打卡',
  other: '其他打卡',
};

/**
 * 中文时长：1800→30分钟、1886→31分钟26秒、6600→1小时50分。
 * 设计稿写的是「用时31分钟26秒」，不能用 formatDuration（那个会四舍五入到分）。
 */
export function formatDurationCn(seconds) {
  const s = Math.floor(Number(seconds || 0));
  if (s <= 0) return null;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}小时${m ? `${m}分钟` : ''}`;
  if (m > 0) return sec ? `${m}分钟${sec}秒` : `${m}分钟`;
  return `${sec}秒`;
}

/** 53.0 → "53"；70.6 → "70.6"（避免体重显示成 53.0 kg）。 */
function trimNumber(v) {
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return String(Number.isInteger(n) ? n : Number(n.toFixed(2)));
}

const hhmm = (t) =>
  t ? moment(t, 'YYYY-MM-DD HH:mm:ss').format('HH:mm') : null;

/**
 * 单条打卡明细 → 视图模型（按设计稿口径逐分类拼）。
 *
 * 稿子五行样本对应到真实字段（SIT 实测）：
 *   到店  越秀丽柏广场（到店训练）    碰一碰/扫码：location=商家名、name=targetInfo
 *   到店  跑步机（用时31分铙26秒）  手动运动到店：name=项目、durationInSeconds、商家另显
 *   训练  入境指导课（用时14分钟）  type=课型中文、name=课程名、durationInSeconds
 *   运动  跑步打卡（用时10分钟）    name=项目中文 + “打卡”；type 是 RUN/99 枚举码，不显示
 *   饮食  饮食打卡 + 图 + 文字      image、content（后端已按图片优先排序）
 *   其他  睡眠打卡（睡觉 17:09）    subCategory + name/startTime；体重用 value+unit；心情用 icon+name
 */
export function toSignItemView(item, category) {
  const name = cleanText(item?.name);
  const merchant = parseLocation(item?.location);
  const dur = formatDurationCn(item?.durationInSeconds);
  /** 图片 + 视频（全 8 类都可能有）：优先后端 media，回落 image */
  const media = toMediaList(item);
  const bodyText = cleanText(item?.content);
  const sub = cleanText(item?.subCategory);

  let title = CATEGORY_LABEL[category] || '打卡';
  let paren = null;
  /** 心情图标：底层给的是图片 URL（不是 emoji），要当图渲染，不能拼进文案 */
  let iconUrl = null;
  /** 商家名：只有“标题不是商家”的到店条目才需要行尾补上 */
  let merchantText = null;

  if (category === 'training') {
    /** 课程类型作标题（稿：入境指导课），括号里：课程名：用时 */
    title = cnText(item?.type) || name || title;
    paren =
      [name && name !== title ? name : null, dur ? `用时${dur}` : null]
        .filter(Boolean)
        .join('：') || null;
  } else if (category === 'sport') {
    /**
     * 运动只取 name（跑步／快走／骑行）拼成「跑步打卡」；
     * type 是底层枚举码（RUN／99；99=其他自定义），显示出来只有噪声（SIT 实测）。
     */
    title = name ? `${name}打卡` : title;
    paren = dur ? `用时${dur}` : null;
  } else if (category === 'offline') {
    if (item?.sourceType === 'offline_interaction') {
      /** 碰一碰/扫码：商家名当标题，targetInfo 当括号（完全对稿「越秀丽柏广场（到店训练）」） */
      title = merchant || name || title;
      paren = merchant && name ? name : null;
      merchantText = null; // 标题已经用了其中一个，行尾再拼就重复
    } else {
      /** 手动运动到店：项目名当标题、用时当括号，商家名行尾补上（PRD：展示所属商家） */
      title = name || title;
      paren = dur ? `用时${dur}` : null;
      merchantText = merchant;
    }
  } else if (category === 'diet') {
    /** 稿：固定「饮食打卡」，下面先图再文字；餐次放括号（后端已按图片优先排序） */
    paren = dietMealLabel(cleanText(item?.type)) || cnText(name);
  } else if (category === 'other') {
    /**
     * 「其他」桶里 subCategory 只有粗分类（体重/睡眠/心情/用药/自由打卡），
     * 但体测类（sourceType=weight）装的是所有围度：SIT 实测
     * {subCategory:"体重", type:"arm", name:"手臂围", value:30, unit:"cm"}，
     * 拿 subCategory 当标题会把「手臂围」说成「体重打卡」→ 标题改取 name。
     */
    const isBodyMeasure = item?.sourceType === 'weight';
    const headName = isBodyMeasure ? cnText(name) : null;
    const head = headName || sub;
    /** subCategory 本身已带「打卡」（自由打卡）时不重复拼后缀 */
    title = head ? (/打卡$/.test(head) ? head : `${head}打卡`) : title;
    if (sub === '睡眠') {
      const start = hhmm(item?.startTime);
      paren =
        [name, start ? `${start} 起` : null, dur ? `共${dur}` : null]
          .filter(Boolean)
          .join(' ') || null;
    } else if (sub === '心情') {
      const rawIcon = cleanText(item?.icon);
      const isImg = rawIcon && /^(https?:)?\/\//i.test(rawIcon);
      if (isImg) iconUrl = rawIcon;
      paren = [isImg ? null : rawIcon, name].filter(Boolean).join('') || null;
    } else if (item?.value !== undefined && item?.value !== null) {
      paren = `${trimNumber(item.value)}${item.unit ? ` ${item.unit}` : ''}`;
    } else {
      /** 标题已经用了 name 就不再括号里重复一遍 */
      paren = name && name !== sub && !headName ? name : null;
    }
  }

  return {
    id: item?.id,
    category,
    /** 行首时间（稿：17:09 在时间轴左边），底层给什么显示什么 */
    timeText: hhmm(item?.recordTime) || '—',
    title,
    /** 括号里的补充：用时／课程名／餐次／数值 */
    paren,
    /** 训练标签：真人 / 入境 / 其它 */
    trainingKind: item?.trainingKind || null,
    subCategory: sub,
    /** 行尾商家名（仅到店且标题不是商家时有值） */
    merchantText,
    /** 心情等分类的图片图标 URL（括号里内联渲染，不进文案） */
    iconUrl,
    /** 媒体列表 [{ kind: 'image'|'video', url, cover }]，按底层原始顺序图文混排 */
    media,
    bodyText,
    recordTime: item?.recordTime || null,
    location: merchant,
    offline: item?.offline,
  };
}

/** 明细分组 → 视图分组（保序，去掉空组）。 */
export function toSignDetailViews(groups) {
  if (!Array.isArray(groups)) return [];
  return groups
    .filter((g) => g && Array.isArray(g.items) && g.items.length)
    .map((g) => ({
      category: g.category,
      categoryLabel: g.categoryLabel || g.category,
      count: Number(g.count || g.items.length),
      items: g.items.map((it) => toSignItemView(it, g.category)),
    }));
}

/* ------------------------------------------------------------------ *
 * 四、本地 mock（结构与真实响应一致，切 USE_SIGN_MOCK 不需要改页面）
 * ------------------------------------------------------------------ */

const MOCK = {
  [API_PATHS.overview]: {
    overview: {
      startDate: '2025-10-13',
      endDate: '2026-10-11',
      weeks: 52,
      totalClassCount: 33,
      totalSignCount: 55,
      offlineDayCount: 12,
      lastOfflineTime: '2026-09-28 19:40:00',
      trainingCount: 40,
      sportCount: 3,
      dietCount: 8,
      offlineCount: 15,
      otherCount: 4,
    },
    heatmap: [
      {
        date: '2026-09-28',
        hasSign: true,
        trainingCount: 2,
        sportCount: 0,
        offlineCount: 2,
        dietCount: 1,
        otherCount: 0,
        totalCount: 5,
      },
      {
        date: '2026-09-27',
        hasSign: true,
        trainingCount: 0,
        sportCount: 1,
        offlineCount: 0,
        dietCount: 0,
        otherCount: 1,
        totalCount: 2,
      },
      {
        date: '2026-01-06',
        hasSign: true,
        trainingCount: 0,
        sportCount: 1,
        offlineCount: 0,
        dietCount: 7,
        otherCount: 3,
        totalCount: 11,
      },
      {
        date: '2025-12-03',
        hasSign: true,
        trainingCount: 5,
        sportCount: 0,
        offlineCount: 0,
        dietCount: 0,
        otherCount: 0,
        totalCount: 5,
      },
    ],
  },
  [API_PATHS.dailyDetail]: {
    userId: 0,
    date: '',
    total: 2,
    groups: [
      {
        category: 'training',
        categoryLabel: '训练打卡',
        count: 1,
        items: [
          {
            id: 1,
            category: 'training',
            sourceType: 'training',
            type: '线下真人指导课',
            trainingKind: '真人',
            name: '燃脂训练-1',
            recordTime: '2026-09-28 19:00:00',
            durationInSeconds: 3600,
            coachId: 56797,
          },
        ],
      },
      {
        category: 'offline',
        categoryLabel: '到店打卡',
        count: 1,
        items: [
          {
            id: 2,
            category: 'offline',
            sourceType: 'offline_interaction',
            type: 'nfc_touch',
            name: '碰一碰到店',
            location: 'XX旗舰店',
            recordTime: '2026-09-28 19:40:00',
          },
        ],
      },
    ],
  },
};
